import React, {useEffect, useState} from "react";
import * as solutionConfig from "src/config/solutionConfigs.json";
import {
    ActionType, AUTO_DISMISS_TIMEOUT,
    EASE_CLIENT_ID,
    FILE_UPLOAD_REPORT_SUFFIX,
    FileExtensions,
    PART_SIZE_5_MB,
    SolutionTypeNames,
    SolutionTypes, StringUtils
} from "src/constant/constants";
import {
    CompleteS3FileUploadAttribute, IAttachmentMetadata, IFileUploaderInput,
    InitiateMultipartUploadAttribute,
    S3CompletedPart, SolutionDetailAttribute,
    UploadFileToS3Attribute
} from "src/entity/entities";
import {completeS3FileUpload, initiateMultipartUpload, uploadFileToS3} from "src/service/service";
import {Stage} from "src/enum/enums";
import {logger} from "src/logger";
import {getStage, isNullOrUndefined, readFileAsBase64} from "src/util/commonUtil";
import {Col, Row} from "@amzn/stencil-react-components/layout";
import {useAppDispatch, useAppSelector} from "src/state/hooks";
import {
    EventModelState,
    SolutionState,
    updateEventRule,
    updateEventSolutionDetailsObject
} from "src/state/slice/EventModelSlice";
import "src/components/authoringComponents/styles/FileUploader.scss";
import {IconUpload, IconCross, IconSize} from "@amzn/stencil-react-components/icons";
import {Spinner, SpinnerColorScheme} from "@amzn/stencil-react-components/spinner";
import {Button, ButtonVariant} from "@amzn/stencil-react-components/button";
import {Link} from "@amzn/stencil-react-components/link";
import {getStageBasedAssetCloudfrontDistribution} from "src/constant/endpoints";
import {Text} from "@amzn/stencil-react-components/text";
import {
    combineJsonLogic, createJsonLogicExpressionForBulkFileUpload,
    csvToJSON, extractBulkFileUploadSegmentationAttributes, extractUploadedFilesMetadata,
    jsonArrayToCSVForCAFileUploadReport, modifyEventRuleOnUploadedBulkFileRemoval,
    readFileContents,
    stringToFileObject,
    validateUploadedData
} from "src/util/fileUtil";
import {
    ContactAlertFileMetadataState,
    setDataToBePublished, setEntityHeader, setExistingBulkUploadFileAttributes, setNewBulkUploadFileAttributes, setIsValidFileFormatForBulkUpload
} from "src/state/slice/ContactAlertFileMetadataSlice";
import {InputFooter} from "@amzn/stencil-react-components/form";

const STRINGS_DISPLAYED = {
    select_file: "Select File",
    bulk_file_supported_format_message: "Bulk File Upload is only supported for .csv file"
}

/**
 * This component is used to handle file upload operations
 */
export const FileUploader = ({attributeName, attributeValue, actionType}: IFileUploaderInput) => {

    const dispatch = useAppDispatch();
    const eventModelState: EventModelState = useAppSelector(({eventModel}) => eventModel);
    const contactAlertFileMetadataState: ContactAlertFileMetadataState = useAppSelector(({contactAlertFileMetadataComponent}) => contactAlertFileMetadataComponent);
    const solutionDetailAttributes: SolutionDetailAttribute[] = solutionConfig[eventModelState?.eventSolution?.type]?.solutionDetailAttributes;

    const [fileUploadAction, setFileUploadAction] = useState<boolean>(true);
    const [uploadedFile, setUploadedFile] = useState<any>(null);
    const [uploadedFileName, setUploadedFileName] = useState<string>(null);
    const [isFileUploading, setIsFileUploading] = useState<boolean>(false);
    const [attachmentsMetadata, setAttachmentsMetadata] = useState<IAttachmentMetadata[]>([]);
    const [bulkFileUploadAttributes, setBulkFileUploadAttributes] = useState<string[]>([]);
    const [isValidFileFormat, setIsValidFileFormat] = useState<boolean>(true);
    let dataToBePublished: any[] = [];

    /**
     * Method to upload asset file to asset management system in chunks
     * @param assetFile the file to be uploaded
     * @param eventModelState the details of event model
     */
    const uploadAssetFile = async (assetFile: any, eventModelState: EventModelState) => {
        const clientId = EASE_CLIENT_ID;
        const partSize: number = PART_SIZE_5_MB;
        const assetFilename = assetFile?.name?.replace(/\s/g, StringUtils.EMPTY);
        let fileContents: string = StringUtils.EMPTY;
        try {
            if (SolutionTypes.CONTACT_ALERT === eventModelState?.eventSolution?.type) {
                if (!assetFilename.endsWith(FileExtensions.CSV)) {
                    setIsValidFileFormat(false);
                    dispatch(setIsValidFileFormatForBulkUpload(false));
                    throw new Error(STRINGS_DISPLAYED.bulk_file_supported_format_message);
                }
            }
            const initiateMultipartUploadResult: InitiateMultipartUploadAttribute =
                await initiateMultipartUpload(getStage() as Stage, clientId, clientId + StringUtils.SLASH_DELIMITER +
                    eventModelState?.eventName + StringUtils.SLASH_DELIMITER + assetFilename);
            let eTags: string[] = [];
            let checksumCRC32s: string[] = [];
            let fileOffset: number = 0;
            let partNumber: number = 1;
            let isFileUploadedInSingleChunk: boolean = false;

            while (fileOffset < assetFile.size) {
                const sliceStart = fileOffset;
                const sliceEnd = Math.min(assetFile.size, fileOffset + partSize);
                const fileSlice = assetFile.slice(sliceStart, sliceEnd);
                const filePart = new File([fileSlice], `part_${partNumber}_${assetFile.name}`);
                const filePartContents: string = await readFileContents(filePart);
                fileContents += filePartContents;
                const base64Data = await readFileAsBase64(filePart);
                const uploadFileToS3Response: UploadFileToS3Attribute =
                    await uploadFileToS3(getStage() as Stage, clientId, clientId + StringUtils.SLASH_DELIMITER + eventModelState?.eventName
                        + StringUtils.SLASH_DELIMITER + assetFilename, initiateMultipartUploadResult?.uploadId, partNumber, filePart.size, base64Data);
                logger.info(`part ${partNumber} uploaded: ${uploadFileToS3Response?.eTag}, ${uploadFileToS3Response?.checkSumCRC32}`);
                eTags.push(uploadFileToS3Response?.eTag);
                checksumCRC32s.push(uploadFileToS3Response?.checkSumCRC32);
                partNumber = partNumber + 1;
                fileOffset = fileOffset + partSize;
            }
            isFileUploadedInSingleChunk = partNumber == 2;
            if (!isFileUploadedInSingleChunk) {
                const uploadedPartsInfo: S3CompletedPart[] = [];
                eTags.forEach((eTag, index) => uploadedPartsInfo.push({
                    eTag: eTag,
                    partNumber: index + 1,
                    checksumCRC32: checksumCRC32s[index]
                }));
                const completeS3FileUploadResponse: CompleteS3FileUploadAttribute =
                    await completeS3FileUpload(getStage() as Stage, clientId, clientId + StringUtils.SLASH_DELIMITER + eventModelState?.eventName
                        + StringUtils.SLASH_DELIMITER + assetFilename, initiateMultipartUploadResult?.uploadId, uploadedPartsInfo);
                logger.info(`file uploaded successfully: ${completeS3FileUploadResponse?.eTag}`)
            }
            if (SolutionTypes.CONTACT_ALERT === eventModelState?.eventSolution?.type) {
                extractUploadedFileData(fileContents, assetFilename, eventModelState);
            }
        } catch (exception) {
            logger.error(`Upload failed for client: ${clientId} with the following error: ${exception}.`);
        }
    }

    useEffect(() => {
        if (eventModelState.eventSolution.type === SolutionTypes.MESSAGE_BOARD) {
            setAttachmentsMetadata(eventModelState.eventSolution.solutionDetailObject["attachments"])
        }
    }, []);

    useEffect(() => {
        try {
            if (eventModelState?.eventSolution?.type === SolutionTypes.CONTACT_ALERT && actionType === ActionType.EDIT) {
                const eventRuleObject = isNullOrUndefined(eventModelState?.eventRule) || eventModelState?.eventRule === StringUtils.EMPTY ? {} : JSON.parse(eventModelState?.eventRule);
                const bulkFileUploadAttributes: string[] = extractBulkFileUploadSegmentationAttributes(eventRuleObject, eventModelState?.eventName);
                dispatch(setNewBulkUploadFileAttributes(bulkFileUploadAttributes));
                setBulkFileUploadAttributes(bulkFileUploadAttributes);
            }
        } catch (error) {
            // suppressing the error
            logger.error("Some error occurred while parsing event rule.");
        }
    }, [eventModelState?.eventRule]);

    const handleBulkFileUpload = (event, solutionType) => {
        if (solutionType === SolutionTypes.MESSAGE_BOARD) {
            setUploadedFile(event.target.files);
            let allFileNames = StringUtils.EMPTY;
            for (let i = 0; i < event.target.files.length; i++) {
                if (i === event.target.files.length - 1) {
                    allFileNames += event.target.files[i]?.name?.replace(/\s/g, StringUtils.EMPTY);
                } else {
                    allFileNames += event.target.files[i]?.name?.replace(/\s/g, StringUtils.EMPTY) + ", ";
                }
            }
            setUploadedFileName(allFileNames);
        } else { // single file upload
            setUploadedFile(event.target.files[0]);
            setUploadedFileName(event.target.files[0]?.name?.replace(/\s/g, StringUtils.EMPTY));
        }
    }

    const extractUploadedFileData = async (fileContents: string, assetFilename: string, eventModelState: EventModelState) => {
        let {jsonData, entityHeader} = csvToJSON(fileContents);
        dispatch(setEntityHeader(entityHeader));
        const {data, validData, invalidData, publish, unPublish} = validateUploadedData(jsonData);
        dispatch(setDataToBePublished(publish));
        dataToBePublished = publish;
        const dataReport: string = jsonArrayToCSVForCAFileUploadReport(data);
        const reportFile: File = stringToFileObject(dataReport, assetFilename + FILE_UPLOAD_REPORT_SUFFIX);
        const eventModelSolutionForReport: SolutionState = {
            type: SolutionTypes.CONTACT_ALERT_REPORTING,
            solutionDetailObject: eventModelState?.eventSolution?.solutionDetailObject
        }
        const eventModelStateForReport: EventModelState = {
            ...eventModelState,
            eventSolution: eventModelSolutionForReport
        }
        dispatch(updateEventSolutionDetailsObject({
            [SolutionTypeNames.FILE_UPLOAD_REPORT]: getStageBasedAssetCloudfrontDistribution(getStage() as Stage) +
            StringUtils.SLASH_DELIMITER + eventModelState?.eventName + StringUtils.SLASH_DELIMITER + assetFilename.trim() + FILE_UPLOAD_REPORT_SUFFIX
        }));
        await uploadAssetFile(reportFile, eventModelStateForReport);
        // TODO: send valid list to upsertAPI which will handle segmentation calls
    }

    const MessageBoardAttachmentMetadata = ({attributeValue, eventModelState, attachmentsMetadata}) => {
        if (
            attributeValue &&
            eventModelState?.eventSolution?.type === SolutionTypes.MESSAGE_BOARD
        ) {
            return (
                <div className={"combined-links-display"}>
                    <Col>
                        {attachmentsMetadata.map((attachmentMetadata) => (
                            <Link
                                href={attachmentMetadata.link}
                                target="_blank"
                                className={"uploaded-file-link"}
                                key={attachmentMetadata.link}
                            >
                                {attachmentMetadata.link}
                            </Link>
                        ))}
                    </Col>
                    <Button
                        icon={<IconCross size={IconSize.Medium} aria-hidden />}
                        variant={ButtonVariant.Tertiary}
                        isDestructive={true}
                        aria-label="Save changes"
                        onClick={async () => {
                            setFileUploadAction(true);
                            dispatch(updateEventSolutionDetailsObject({
                                [attributeName]: StringUtils.EMPTY,
                            }));
                        }}
                    />
                </div>
            );
        }
        return null;
    }

    const uploadFile = async () => {
        if (eventModelState?.eventSolution?.type == SolutionTypes.CONTACT_ALERT) {
            if (uploadedFile) {
                setIsFileUploading(true);
                await uploadAssetFile(uploadedFile, eventModelState);
                setIsFileUploading(false);
                setFileUploadAction(false);
                dispatch(updateEventSolutionDetailsObject({
                    [attributeName]: getStageBasedAssetCloudfrontDistribution(getStage() as Stage) + StringUtils.SLASH_DELIMITER +
                    eventModelState?.eventName + StringUtils.SLASH_DELIMITER + uploadedFileName.trim(),
                }));
                if (attributeName === SolutionTypeNames.BULK_FILE_UPLOAD) {
                    const updatedEventRule = combineJsonLogic(eventModelState, createJsonLogicExpressionForBulkFileUpload(eventModelState, dataToBePublished));
                    dispatch(updateEventRule(JSON.stringify(updatedEventRule)));
                    const eventRuleObject = isNullOrUndefined(JSON.stringify(updatedEventRule)) || JSON.stringify(updatedEventRule) === StringUtils.EMPTY ? {} : updatedEventRule;
                    const bulkFileUploadAttributes: string[] = extractBulkFileUploadSegmentationAttributes(eventRuleObject, eventModelState?.eventName);
                    dispatch(setNewBulkUploadFileAttributes(bulkFileUploadAttributes));
                    setBulkFileUploadAttributes(bulkFileUploadAttributes);
                }
            }
        } else if (eventModelState?.eventSolution?.type == SolutionTypes.MESSAGE_BOARD) {
            if (uploadedFile) {
                setIsFileUploading(true);
                for (const file of Array.from(uploadedFile)) {
                    await uploadAssetFile(file, eventModelState);
                }
                setIsFileUploading(false);
                setFileUploadAction(false);
                await setAttachmentsMetadata(extractUploadedFilesMetadata(uploadedFileName, eventModelState));
                dispatch(updateEventSolutionDetailsObject({
                    [attributeName]: extractUploadedFilesMetadata(uploadedFileName, eventModelState)
                }));
            }
        }
    }

    const clearUploadedFile = async () => {
        setFileUploadAction(true);
        dispatch(updateEventSolutionDetailsObject({
            [attributeName]: StringUtils.EMPTY,
        }));
        if (SolutionTypeNames.BULK_FILE_UPLOAD === attributeName) {
            dispatch(updateEventSolutionDetailsObject({
                [SolutionTypeNames.FILE_UPLOAD_REPORT]: StringUtils.EMPTY,
            }));
            const eventModelObject = isNullOrUndefined(eventModelState?.eventRule) ? {} : JSON.parse(eventModelState?.eventRule);
            dispatch(setExistingBulkUploadFileAttributes(contactAlertFileMetadataState?.newBulkUploadFileAttributes));
            const modifiedEventModel = modifyEventRuleOnUploadedBulkFileRemoval(eventModelObject, bulkFileUploadAttributes);
            dispatch(updateEventRule(JSON.stringify(modifiedEventModel)));
        }
        setIsValidFileFormat(true);
        dispatch(setIsValidFileFormatForBulkUpload(true));
    }

    return <>
        {
            attributeValue === StringUtils.EMPTY &&
            fileUploadAction ?
                <Row className={"select-file-container"}>
                    <Row className={"select-file-button-wrap"}>
                        <label className="select-file-button"
                               htmlFor={`${attributeName}-input`}>
                            {STRINGS_DISPLAYED.select_file}
                        </label>
                        <input id={`${attributeName}-input`} multiple={eventModelState?.eventSolution?.type === SolutionTypes.MESSAGE_BOARD}
                               type="file" onChange={async (event) => handleBulkFileUpload(event, eventModelState?.eventSolution?.type)}
                        />
                        { !isFileUploading ?
                            <Button
                                className={"upload-icon"}
                                icon={<IconUpload size={IconSize.Medium} aria-hidden />}
                                variant={ButtonVariant.Primary}
                                aria-label="Save changes"
                                onClick={uploadFile}
                                disabled={!fileUploadAction}
                            /> :
                            <Spinner colorScheme={SpinnerColorScheme.Default}/>
                        }
                        <Text className={"selected-file-name"} fontSize={14}>
                            {uploadedFileName}
                        </Text>
                    </Row>
                </Row> :
                attributeValue && eventModelState?.eventSolution?.type !== SolutionTypes.MESSAGE_BOARD ?
                <Row>
                    <Link
                        href={attributeValue}
                        target="_blank"
                        className={"uploaded-file-link"}
                    >
                        {attributeValue}
                    </Link>
                    <Button
                        icon={<IconCross size={IconSize.Medium} aria-hidden />}
                        variant={ButtonVariant.Tertiary}
                        isDestructive={true}
                        aria-label="Save changes"
                        onClick={clearUploadedFile}
                    />

                    {!isValidFileFormat && <InputFooter id="input-footer-1" className={"width_200_px"} error>
                        {STRINGS_DISPLAYED.bulk_file_supported_format_message}
                    </InputFooter>
                    }
                </Row>
                    :
                    <MessageBoardAttachmentMetadata attributeValue={attributeValue}
                                                    eventModelState={eventModelState}
                                                    attachmentsMetadata={attachmentsMetadata}
                    />
        }
    </>
}
